import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { USER_PROFILE, ACADEMIC_YEARS, STUDENT_SUBMIT_PARENT_EMAIL } from '../config/Config';
import { filterPublished } from '../utils/publishedFilter';
import StudentSelfConsentSection from '../components/legal/StudentSelfConsentSection';

export default function StudentCompleteProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [step, setStep] = useState('profile');

  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [academicYearId, setAcademicYearId] = useState(user?.academic_year?.id ? String(user.academic_year.id) : '');
  const [parentEmail, setParentEmail] = useState(user?.parent_email || '');
  const [useOtpLoginOnly, setUseOtpLoginOnly] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [studentSelfConsent, setStudentSelfConsent] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [legalDocumentAvailable, setLegalDocumentAvailable] = useState(null);

  const isStudent = user?.is_student || user?.isStudent || (user?.role_name && String(user.role_name).toLowerCase() === 'student');
  const needsProfile = !!user?.requires_profile_completion;
  const requiresGrade = !!user?.requires_grade_selection;
  const requiresConsent = !!user?.requires_parent_consent;

  useEffect(() => {
    if (step === 'done') {
      return;
    }
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (!needsProfile && !requiresGrade && !requiresConsent) {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (requiresConsent && !needsProfile && !requiresGrade) {
      setStep('parent');
    } else {
      setStep('profile');
    }
  }, [step, user, needsProfile, requiresGrade, requiresConsent, navigate]);

  useEffect(() => {
    if (step !== 'done') {
      return undefined;
    }
    const timer = setTimeout(() => {
      navigate('/', { replace: true });
    }, 3000);
    return () => clearTimeout(timer);
  }, [step, navigate]);

  useEffect(() => {
    const fetchAcademicYears = async () => {
      try {
        const res = await api.get(`${ACADEMIC_YEARS}?published_only=true`);
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setAcademicYears(filterPublished(list));
      } catch (e) {
        setError('Failed to load grades');
      } finally {
        setLoading(false);
      }
    };

    if (isStudent) {
      fetchAcademicYears();
    } else {
      setLoading(false);
    }
  }, [isStudent]);

  const refreshProfile = async () => {
    const profileRes = await api.get(USER_PROFILE);
    if (setUser) setUser(profileRes.data);
    if (localStorage.getItem('token')) {
      localStorage.setItem('user', JSON.stringify(profileRes.data));
    }
    if (sessionStorage.getItem('token')) {
      sessionStorage.setItem('user', JSON.stringify(profileRes.data));
    }
    return profileRes.data;
  };

  const handleCompleteProfile = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required.');
      return;
    }
    if (isStudent && !academicYearId) {
      setError('Please select your grade.');
      return;
    }
    if (!useOtpLoginOnly) {
      if (!password || !confirmPassword) {
        setError('Password and confirm password are required unless you choose OTP-based login.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    setError('');
    setSuccessMessage('');
    setSubmitting(true);
    try {
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        complete_registration_profile: true,
        use_otp_login_only: useOtpLoginOnly,
      };
      if (!useOtpLoginOnly) {
        payload.password = password;
        payload.password2 = confirmPassword;
      }
      if (isStudent) {
        payload.academic_year_id = academicYearId;
      }

      await api.patch(USER_PROFILE, payload);
      const updatedUser = await refreshProfile();

      if (updatedUser?.requires_parent_consent) {
        setStep('parent');
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      const data = err?.response?.data;
      const firstKey = data && typeof data === 'object' ? Object.keys(data)[0] : null;
      const firstValue = firstKey ? data[firstKey] : null;
      setError(
        data?.error ||
        data?.detail ||
        (Array.isArray(firstValue) ? firstValue[0] : firstValue) ||
        'Failed to save your profile.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitParentEmail = async (e) => {
    e.preventDefault();
    const selfAttested = !!studentSelfConsent;
    const email = selfAttested
      ? (user?.email || '').trim().toLowerCase()
      : (parentEmail || '').trim().toLowerCase();
    const studentEmail = (user?.email || '').trim().toLowerCase();
    if (!email) {
      setError(selfAttested ? 'Your student email is missing.' : 'Please enter the parent/guardian email.');
      return;
    }
    if (!selfAttested && studentEmail && email === studentEmail) {
      setError('Parent email must be different from your student account email. Please enter a parent or guardian email.');
      return;
    }
    if (selfAttested && !agreeToTerms) {
      setError('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await api.post(STUDENT_SUBMIT_PARENT_EMAIL, {
        parent_email: email,
        is_same_as_student: selfAttested,
        agreeToTerms: selfAttested ? agreeToTerms : undefined,
        student_self_consent: selfAttested,
      });
      setSuccessMessage(res.data?.message || 'Please check the email we sent and confirm.');
      const updatedUser = await refreshProfile();
      setParentEmail(email);
      if (res.data?.same_email || !updatedUser?.requires_parent_consent) {
        navigate('/dashboard', { replace: true });
      } else {
        setStep('done');
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to send email.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', py: 4, px: 2 }}>
      <Paper elevation={2} sx={{ p: 3.5, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
          Complete your profile
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Finish the essentials now. You can update the rest of your profile later.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {step === 'profile' && (
          <form onSubmit={handleCompleteProfile}>
            <TextField
              fullWidth
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            {isStudent && (
              <TextField
                select
                fullWidth
                label="Grade"
                value={academicYearId}
                onChange={(e) => setAcademicYearId(e.target.value)}
                required
                SelectProps={{ 'data-testid': 'student-grade-dropdown' }}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">Select grade</MenuItem>
                {academicYears.map((y) => (
                  <MenuItem key={y.id} value={String(y.id)}>
                    {y.year}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={useOtpLoginOnly}
                  onChange={(e) => setUseOtpLoginOnly(e.target.checked)}
                />
              }
              label="I will use OTP based login"
              sx={{ mb: 2 }}
            />

            {!useOtpLoginOnly && (
              <>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Confirm password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
              </>
            )}

            <Button type="submit" variant="contained" disabled={submitting} fullWidth>
              {submitting ? <CircularProgress size={24} /> : 'Save and continue'}
            </Button>
          </form>
        )}

        {step === 'parent' && (
          <form onSubmit={handleSubmitParentEmail}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              For students in grade 10 or below we need parent or guardian consent. Enter a parent or guardian email, or confirm that you are the parent or a student aged 16+ to complete this step on the same email.
            </Typography>

            <StudentSelfConsentSection
              studentSelfConsent={studentSelfConsent}
              agreeToTerms={agreeToTerms}
              onStudentSelfConsentChange={(checked) => {
                setStudentSelfConsent(checked);
                if (!checked) {
                  setAgreeToTerms(false);
                }
              }}
              onAgreeToTermsChange={setAgreeToTerms}
              onLegalAvailabilityChange={setLegalDocumentAvailable}
              consentTestId="complete-profile-student-self-consent"
              termsTestId="complete-profile-terms-checkbox"
            />

            {!studentSelfConsent && (
              <TextField
                fullWidth
                label="Parent/guardian email"
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                required
                helperText="Must be different from your student account email."
                inputProps={{ 'data-testid': 'parent-email-input' }}
                sx={{ mb: 2 }}
              />
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={submitting || (studentSelfConsent && legalDocumentAvailable !== true)}
              fullWidth
              data-testid="parent-email-submit"
            >
              {submitting ? <CircularProgress size={24} /> : (studentSelfConsent ? 'Accept terms and continue' : 'Send consent request')}
            </Button>
          </form>
        )}

        {step === 'done' && (
          <Box>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              We sent an email to confirm parent or guardian consent. Once it is approved, your account will be ready to use.
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Redirecting you to the home page...
            </Typography>
            <Button variant="contained" onClick={() => navigate('/', { replace: true })} fullWidth>
              Go to home page
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
